import { useEffect, useState } from "react";
import axios from "axios";
import { ScrollText, DollarSign } from "lucide-react";
import { Section, Kpi, money } from "@/mockups/dc";
import { RAPI, act, when, Th } from "./common";

export const ActivityTab = () => {
  const [f, setF] = useState({ location_id: "", person: "", type: "" });
  const [skip, setSkip] = useState(0);
  const [d, setD] = useState(null);
  const [locs, setLocs] = useState([]);
  useEffect(() => { axios.get(`${RAPI}/tenants`).then((r) => setLocs(r.data.rows)); }, []);
  useEffect(() => {
    const p = new URLSearchParams({ ...Object.fromEntries(Object.entries(f).filter(([, v]) => v)), skip, limit: 50 });
    const t = setTimeout(() => axios.get(`${RAPI}/activity?${p}`).then((r) => setD(r.data)), 250);
    return () => clearTimeout(t);
  }, [f, skip]);
  const set = (k) => (e) => { setSkip(0); setF({ ...f, [k]: e.target.value }); };
  const names = Object.fromEntries(locs.map((l) => [l.id, l.name]));
  return (
    <Section title="Activity log" sub="Logins, settings changes, approvals, Gingr connections, campaign sends, agent runs and invites." icon={ScrollText} testid="activity-log">
      <div style={{ display: "flex", gap: 6, flexWrap: "wrap", marginBottom: 12 }}>
        <select className="input" value={f.location_id} onChange={set("location_id")} data-testid="activity-filter-location"><option value="">All locations</option>{locs.map((l) => <option key={l.id} value={l.id}>{l.name}</option>)}</select>
        <input className="input" placeholder="Person (email)" value={f.person} onChange={set("person")} data-testid="activity-filter-person" />
        <select className="input" value={f.type} onChange={set("type")} data-testid="activity-filter-type"><option value="">All types</option>{(d?.types || []).map((t) => <option key={t} value={t}>{t.replace(/_/g, " ")}</option>)}</select>
      </div>
      {!d ? <div className="skeleton" style={{ height: 200 }} /> : (
        <>
          <table className="table" style={{ width: "100%" }}>
            <Th cols={["When", "Type", "What happened", "Who", "Location"]} />
            <tbody>{d.items.map((i) => (
              <tr key={i.id} data-testid={`activity-row-${i.id}`}><td className="muted mono" style={{ fontSize: 12, whiteSpace: "nowrap" }}>{when(i.at)}</td><td><span className="chip" style={{ fontSize: 10 }}>{i.type.replace(/_/g, " ")}</span></td>
                <td style={{ fontSize: 13 }}>{i.summary}</td><td className="mono" style={{ fontSize: 11 }}>{i.user_email || "system"}</td><td style={{ fontSize: 12 }}>{names[i.location_id] || (i.location_id ? "—" : "All")}</td></tr>
            ))}</tbody>
          </table>
          <div style={{ display: "flex", gap: 8, alignItems: "center", marginTop: 10 }}>
            <span className="muted mono" style={{ fontSize: 12 }} data-testid="activity-count">{d.total ? `${skip + 1}–${Math.min(skip + 50, d.total)} of ${d.total}` : "No activity yet"}</span>
            <button className="btn btn-sm" disabled={!skip} onClick={() => setSkip(Math.max(0, skip - 50))} data-testid="activity-prev">Previous</button>
            <button className="btn btn-sm" disabled={skip + 50 >= d.total} onClick={() => setSkip(skip + 50)} data-testid="activity-next">Next</button>
          </div>
        </>
      )}
    </Section>
  );
};

const SETTINGS = [["default_price", "Default price / mo"], ["hosting_per_loc", "Hosting share"], ["db_per_loc", "Database share"], ["browser_per_min", "Browser agent $ / min"], ["email_cost", "$ per email"], ["default_ai_cap", "Default AI cap"]];

export const CostsTab = () => {
  const [month, setMonth] = useState(new Date().toISOString().slice(0, 7));
  const [d, setD] = useState(null);
  const load = () => axios.get(`${RAPI}/costs?month=${month}`).then((r) => setD(r.data));
  useEffect(() => { load(); }, [month]); // eslint-disable-line react-hooks/exhaustive-deps
  if (!d) return <div className="skeleton" style={{ height: 240, borderRadius: 12 }} />;
  const t = d.totals;
  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 16 }}>
      <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(180px, 1fr))", gap: 12 }}>
        <Kpi label="Plan revenue" value={money(t.price)} testid="costs-kpi-price" />
        <Kpi label="Running cost" value={money(t.running_cost)} testid="costs-kpi-cost" />
        <Kpi label="Margin" value={money(t.margin)} sub={t.price ? `${Math.round((t.margin / t.price) * 100)}%` : ""} status={t.margin < 0 ? "neg" : undefined} testid="costs-kpi-margin" />
        <Kpi label="AI cost" value={`$${t.ai_cost.toFixed(2)}`} testid="costs-kpi-ai" />
      </div>
      <Section title="Usage and cost per location" sub="AI cost is measured per question; hosting and database are your shares from the settings below. Texts go through each location's own Gingr." icon={DollarSign} testid="costs-table"
        right={<input type="month" className="input" value={month} onChange={(e) => setMonth(e.target.value)} data-testid="costs-month" />}>
        <div style={{ overflowX: "auto" }}>
          <table className="table" style={{ width: "100%" }}>
            <Th cols={["Location", "AI questions", "AI cost", "Emails", "Texts", "Agent runs", "Browser min", "Running cost", "Price", "Margin"]} />
            <tbody>{d.rows.map((r) => (
              <tr key={r.location_id} data-testid={`costs-row-${r.location_id}`}><td><b>{r.name}</b></td><td className="mono">{r.ai_questions}</td><td className="mono">${r.ai_cost.toFixed(2)}</td><td className="mono">{r.emails}</td><td className="mono">{r.texts}</td>
                <td className="mono">{r.agent_runs}</td><td className="mono">{r.browser_minutes}</td><td className="mono">${r.running_cost.toFixed(2)}</td><td className="mono">{money(r.price)}</td>
                <td className="mono" style={{ fontWeight: 700, color: r.margin < 0 ? "var(--neg-ink)" : "var(--pos-ink)" }}>${r.margin.toFixed(2)}</td></tr>
            ))}</tbody>
          </table>
        </div>
        <div style={{ display: "flex", gap: 10, flexWrap: "wrap", marginTop: 14, alignItems: "flex-end" }} data-testid="costs-settings">
          {SETTINGS.map(([k, l]) => (
            <label key={`${k}-${d.settings[k]}`} style={{ display: "flex", flexDirection: "column", gap: 2, fontSize: 11 }} className="muted">{l}
              <input type="number" step="any" className="input mono" style={{ width: 110 }} defaultValue={d.settings[k]} onBlur={(e) => Number(e.target.value) !== d.settings[k] && act(() => axios.patch(`${RAPI}/settings`, { [k]: Number(e.target.value) }), "Saved", load)} data-testid={`costs-setting-${k}`} />
            </label>
          ))}
        </div>
      </Section>
    </div>
  );
};
